/* eslint-disable no-unsafe-optional-chaining */

/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddIcon, DeleteForeverIcon, EditIcon } from 'src/v3/core/icons'

import * as yup from 'yup'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectActiveCourse,
  fetchAccreditations,
  selectAccreditationsByCourseID,
  selectAccreditationsStatus,
} from 'src/v3/create/create.slice'

import { useFormik } from 'formik'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import { Accreditation, AccreditationType } from 'src/v3/core/types'
import {
  SelectLinkedService,
  SelectSection,
  SelectCourse,
} from 'src/v3/create/components/select-entities'
import ConfirmAction from 'src/v3/components/confirm-action'
import useSession from 'src/authentication/hooks/use-session'
import Loading from 'src/v3/components/loading'

let accreditationTypes: AccreditationType[] = [
  'notAccredited',
  'capce',
  'ceBroker',
  'newJersey',
  'newYork',
  'virginia',
]

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let session = useSession()

  let activeCourse = useAppSelector(selectActiveCourse)
  let accreditations = useAppSelector(
    selectAccreditationsByCourseID(activeCourse),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let isAdmin = session?.roles.includes('admin')

  let form = useFormik({
    validationSchema: yup.object({
      accreditationType: yup.string().not(['unselected']).required(),
      courseCode: yup.string(),
      topic: yup.string(),
      subTopic: yup.string(),
      topicArea: yup.string(),
      areaNumberALS: yup.string(),
      areaNumberBLS: yup.string(),
      professionCode: yup.string(),
      subjectArea: yup.string(),
      state: yup.string(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      accreditationType: 'unselected',
      courseCode: '',
      topic: '',
      subTopic: '',
      topicArea: '',
      areaNumberALS: '',
      areaNumberBLS: '',
      professionCode: '',
      subjectArea: '',
      state: '',
    },
    onSubmit: (values) => {
      if (values.accreditationType === 'unselected') {
        setErrorMessage('Accreditation Type must be selected')
        return
      }
      setSaving(true)
      let baseAccreditation: Omit<
        Accreditation[AccreditationType],
        'accreditationType'
      > = {
        courseID: activeCourse,
        courseCode: values.courseCode.trim(),
      }
      if (values.accreditationType === 'virginia') {
        baseAccreditation = {
          courseID: activeCourse,
          topic: values.topic.trim(),
          subTopic: values.subTopic.trim(),
          areaNumberALS: values.areaNumberALS.trim(),
          areaNumberBLS: values.areaNumberBLS.trim(),
        }
      } else if (values.accreditationType === 'ceBroker') {
        baseAccreditation = {
          courseID: activeCourse,
          courseCode: values.courseCode.trim(),
          professionCode: values.professionCode.trim(),
          subjectArea: values.subjectArea.trim(),
          state: values.state,
        }
      } else if (values.accreditationType === 'newYork') {
        baseAccreditation = {
          courseID: activeCourse,
          topicArea: values.topicArea,
        }
      }
      if (!values.id) {
        axios
          .post('/api/v2/create/accreditation', {
            [values.accreditationType]: baseAccreditation,
          })
          .then(() => dispatch(fetchAccreditations()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Accreditation')
            dispatch(fetchAccreditations())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/accreditation', {
            [values.accreditationType]: {
              id: values.id,
              ...baseAccreditation,
            },
          })
          .then(() => dispatch(fetchAccreditations()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Accreditation')
            dispatch(fetchAccreditations())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  let hasAccreditation = (accreditationType: AccreditationType) =>
    accreditations.some(
      (a) =>
        a[accreditationType]?.courseID === activeCourse &&
        (!form.values.id || a[accreditationType]?.id !== form.values.id),
    )

  let onEdit = (id: string) => {
    let accreditation = accreditations.find((a) => {
      if (a.capce) return a.capce.id === id
      if (a.newJersey) return a.newJersey.id === id
      if (a.newYork) return a.newYork.id === id
      if (a.virginia) return a.virginia.id === id
      if (a.ceBroker) return a.ceBroker.id === id
      if (a.notAccredited) return a.notAccredited.id === id
      return false
    })
    form.setFieldValue(
      'id',
      accreditation?.newJersey?.id ||
        accreditation?.newYork?.id ||
        accreditation?.virginia?.id ||
        accreditation?.capce?.id ||
        accreditation?.ceBroker?.id ||
        accreditation?.notAccredited?.id,
    )
    if (accreditation?.virginia) {
      form.setFieldValue('topic', accreditation.virginia.topic)
      form.setFieldValue('subTopic', accreditation.virginia.subTopic)
      form.setFieldValue('areaNumberALS', accreditation.virginia.areaNumberALS)
      form.setFieldValue('areaNumberBLS', accreditation.virginia.areaNumberBLS)
    } else if (accreditation?.ceBroker) {
      form.setFieldValue('courseCode', accreditation.ceBroker.courseCode)
      form.setFieldValue(
        'professionCode',
        accreditation.ceBroker.professionCode,
      )
      form.setFieldValue('subjectArea', accreditation.ceBroker.subjectArea)
      form.setFieldValue('state', accreditation.ceBroker.state)
    } else if (accreditation?.newYork) {
      form.setFieldValue('topicArea', accreditation.newYork.topicArea)
    } else {
      form.setFieldValue(
        'courseCode',
        accreditation?.capce?.courseCode ||
          accreditation?.notAccredited?.courseCode ||
          '',
      )
    }
    if (accreditation) {
      form.setFieldValue(
        'accreditationType',
        accreditationTypes.find((at) => accreditation![at]) || 'unselected',
      )
    }
    setFormOpen(true)
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Accreditations
        </Typography>
        <Typography
          className="ml-3 d-inline"
          sx={{
            marginBottom: '16px',
          }}
        >
          Create an accreditation or select one to edit
        </Typography>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            margin: '16px 0',
          }}
        >
          <SelectLinkedService />
          <SelectSection />
          <SelectCourse />
        </div>
        <div>
          <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Box>
              <Paper
                sx={{
                  p: 2,
                  margin: 'auto',
                  flexGrow: 1,
                }}
              >
                <Box className="grid gap-4">
                  <Box className="flex gap-4">
                    <Box className="flex-1 flex flex-col gap-4">
                      <Box className="flex-1">
                        <Typography
                          gutterBottom
                          variant="subtitle1"
                          component="div"
                        >
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Skeleton animation={false} />
                        </Typography>
                      </Box>
                      <Box>
                        <div className="flex flex-row-reverse">
                          <Button
                            className="ml-3"
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={compose(setFormOpen, T)}
                            disabled={
                              activeCourse === 'unselected' ||
                              (hasAccreditation('notAccredited') && !isAdmin)
                            }
                          >
                            Add New Accreditation
                          </Button>
                        </div>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
            {accreditations.map((a, i) => {
              let aType: AccreditationType = 'notAccredited'
              if (a.capce) aType = 'capce'
              if (a.ceBroker) aType = 'ceBroker'
              if (a.newJersey) aType = 'newJersey'
              if (a.newYork) aType = 'newYork'
              if (a.virginia) aType = 'virginia'
              let id = a[aType]?.id || ''

              return (
                <Box key={i}>
                  <Paper
                    sx={{
                      p: 2,
                      margin: 'auto',
                      flexGrow: 1,
                    }}
                    style={{
                      boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                    }}
                  >
                    <Box className="grid gap-4">
                      <Box className="flex gap-4">
                        <Box className="flex-1 flex flex-col gap-4">
                          <Box className="flex-1">
                            <Typography variant="h6">
                              {
                                {
                                  notAccredited: 'Not Accredited',
                                  capce: 'CAPCE',
                                  ceBroker: 'CEBroker',
                                  newJersey: 'New Jersey',
                                  newYork: 'New York',
                                  virginia: 'Virginia',
                                }[aType]
                              }
                            </Typography>
                            {a.newJersey && (
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                              >
                                Class ID:{' '}
                                {a.newJersey?.courseCode ||
                                  a.newJersey?.courseCode}
                              </Typography>
                            )}
                            {a.newYork && (
                              <>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                  className="capitalize"
                                >
                                  Topic Area: {a.newYork.topicArea}
                                </Typography>
                              </>
                            )}
                            {a.virginia && (
                              <>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Area Number ALS: {a.virginia.areaNumberALS}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Area Number BLS: {a.virginia.areaNumberBLS}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Topic: {a.virginia.topic}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Subtopic: {a.virginia.subTopic}
                                </Typography>
                              </>
                            )}
                            {a.ceBroker && (
                              <>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Course Code: {a.ceBroker.courseCode}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Profession Code: {a.ceBroker.professionCode}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  Subject Area: {a.ceBroker.subjectArea}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                >
                                  State:{' '}
                                  {a.ceBroker.state[0]?.toUpperCase() +
                                    a.ceBroker.state?.slice(1)}
                                </Typography>
                              </>
                            )}
                            {(a.capce || a.notAccredited) && (
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                              >
                                Course Code:{' '}
                                {a.capce?.courseCode ||
                                  a.notAccredited?.courseCode}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <div className="flex flex-row-reverse">
                              <Tooltip title="Delete">
                                <Button
                                  variant="outlined"
                                  className="ml-3"
                                  onClick={() => setDeleting(`delete-${id}`)}
                                  disabled={accreditations.length === 1}
                                >
                                  <DeleteForeverIcon />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <Button
                                  variant="outlined"
                                  className="ml-3"
                                  onClick={() => onEdit(id)}
                                >
                                  <EditIcon />
                                </Button>
                              </Tooltip>
                            </div>
                          </Box>
                          <ConfirmAction
                            executeAction={() =>
                              axios
                                .delete(
                                  `/api/v2/create/delete-accreditation?accreditationID=${id}`,
                                )
                                .then(() => dispatch(fetchAccreditations()))
                            }
                            open={deleting}
                            setOpen={setDeleting}
                            openText={`delete-${id}`}
                            title="Confirm Deletion"
                            text={`Are you sure you want to delete this ${aType} accreditation?`}
                            onFailure={(err) =>
                              setErrorMessage(
                                err.message || 'Error Deleting Accreditation',
                              )
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )
            })}
          </Box>
        </div>

        <Drawer
          anchor="bottom"
          open={formOpen && !saving}
          onClose={compose(setFormOpen, F)}
        >
          <Container maxWidth="sm">
            <Card className="m-4">
              <form className="p-4" onSubmit={form.handleSubmit}>
                <FormControl fullWidth variant="standard" className="mt-4">
                  <InputLabel>Accreditation Type</InputLabel>
                  <Select
                    value={form.values.accreditationType}
                    label="Accreditation Type"
                    name="accreditationType"
                    onChange={form.handleChange}
                    error={
                      form.touched.accreditationType &&
                      !!form.errors.accreditationType
                    }
                  >
                    <MenuItem value="unselected">Unselected</MenuItem>
                    {!hasAccreditation('notAccredited') && (
                      <MenuItem value="notAccredited">Not Accredited</MenuItem>
                    )}
                    {isAdmin && !hasAccreditation('capce') && (
                      <MenuItem value="capce">CAPCE</MenuItem>
                    )}
                    {isAdmin && <MenuItem value="ceBroker">CEBroker</MenuItem>}
                    {isAdmin && (
                      <MenuItem value="newJersey">New Jersey</MenuItem>
                    )}
                    {isAdmin && <MenuItem value="newYork">New York</MenuItem>}
                    {isAdmin && <MenuItem value="virginia">Virginia</MenuItem>}
                  </Select>
                </FormControl>

                {(form.values.accreditationType === 'capce' ||
                  form.values.accreditationType === 'notAccredited' ||
                  form.values.accreditationType === 'newJersey' ||
                  form.values.accreditationType === 'ceBroker') && (
                  <TextField
                    fullWidth
                    className="mt-4"
                    variant="standard"
                    value={form.values.courseCode}
                    label={
                      form.values.accreditationType === 'newJersey'
                        ? 'Class ID'
                        : 'Course Code'
                    }
                    name="courseCode"
                    onChange={form.handleChange}
                    error={form.touched.courseCode && !!form.errors.courseCode}
                  />
                )}

                {form.values.accreditationType === 'virginia' && (
                  <>
                    <TextField
                      fullWidth
                      className="mt-4"
                      variant="standard"
                      value={form.values.topic}
                      label="Topic"
                      name="topic"
                      onChange={form.handleChange}
                      error={form.touched.topic && !!form.errors.topic}
                    />
                    <TextField
                      fullWidth
                      className="mt-4"
                      variant="standard"
                      value={form.values.subTopic}
                      label="Sub-Topic"
                      name="subTopic"
                      onChange={form.handleChange}
                      error={form.touched.subTopic && !!form.errors.subTopic}
                    />
                    <TextField
                      fullWidth
                      className="mt-4"
                      variant="standard"
                      value={form.values.areaNumberALS}
                      label="Area Number ALS"
                      name="areaNumberALS"
                      onChange={form.handleChange}
                      error={
                        form.touched.areaNumberALS &&
                        !!form.errors.areaNumberALS
                      }
                    />
                    <TextField
                      fullWidth
                      className="mt-4"
                      variant="standard"
                      value={form.values.areaNumberBLS}
                      label="Area Number BLS"
                      name="areaNumberBLS"
                      onChange={form.handleChange}
                      error={
                        form.touched.areaNumberBLS &&
                        !!form.errors.areaNumberBLS
                      }
                    />
                  </>
                )}

                {form.values.accreditationType === 'ceBroker' && (
                  <>
                    <TextField
                      fullWidth
                      className="mt-4"
                      variant="standard"
                      value={form.values.professionCode}
                      label="Profession Code"
                      name="professionCode"
                      onChange={form.handleChange}
                      error={
                        form.touched.professionCode &&
                        !!form.errors.professionCode
                      }
                    />
                    <TextField
                      fullWidth
                      className="mt-4"
                      variant="standard"
                      value={form.values.subjectArea}
                      label="Subject Area"
                      name="subjectArea"
                      onChange={form.handleChange}
                      error={
                        form.touched.subjectArea && !!form.errors.subjectArea
                      }
                    />
                    <FormControl fullWidth variant="standard" className="mt-4">
                      <InputLabel>State</InputLabel>
                      <Select
                        value={form.values.state}
                        label="State"
                        name="state"
                        onChange={form.handleChange}
                        error={form.touched.state && !!form.errors.state}
                      >
                        <MenuItem value="Florida">Florida</MenuItem>
                        <MenuItem value="Kentucky">Kentucky</MenuItem>
                        <MenuItem value="Tennessee">Tennessee</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}

                {form.values.accreditationType === 'newYork' && (
                  <>
                    <FormControl fullWidth variant="standard" className="mt-4">
                      <InputLabel>Topic Area</InputLabel>
                      <Select
                        value={form.values.topicArea}
                        label="Topic Area"
                        name="topicArea"
                        onChange={form.handleChange}
                        error={
                          form.touched.topicArea && !!form.errors.topicArea
                        }
                      >
                        <MenuItem value="Preparatory">Preparatory</MenuItem>
                        <MenuItem value="Airway">Airway</MenuItem>
                        <MenuItem value="Pharmacology | Med Admin | Emergency Meds">
                          Pharmacology | Med Admin | Emergency Meds
                        </MenuItem>
                        <MenuItem value="Immunology">Immunology</MenuItem>
                        <MenuItem value="Toxicology">Toxicology</MenuItem>
                        <MenuItem value="Endocrine">Endocrine</MenuItem>
                        <MenuItem value="Neurology">Neurology</MenuItem>
                        <MenuItem value="Abdominal | Geni-Renal | GI | Hematology">
                          Abdominal | Geni-Renal | GI | Hematology
                        </MenuItem>
                        <MenuItem value="Respiratory">Respiratory</MenuItem>
                        <MenuItem value="Psychiatric">Psychiatric</MenuItem>
                        <MenuItem value="Cardiology">Cardiology</MenuItem>
                        <MenuItem value="Shock and Resuscitation">
                          Shock and Resuscitation
                        </MenuItem>
                        <MenuItem value="Trauma">Trauma</MenuItem>
                        <MenuItem value="Geriatrics">Geriatrics</MenuItem>
                        <MenuItem value="OB | Neonate | Pediatrics">
                          OB | Neonate | Pediatrics
                        </MenuItem>
                        <MenuItem value="Special Needs Pt">
                          Special Needs Pt
                        </MenuItem>
                        <MenuItem value="EMS Operations">
                          EMS Operations
                        </MenuItem>
                        <MenuItem value="Mental Health of EMT">
                          Mental Health of EMT
                        </MenuItem>
                        <MenuItem value="Patient Lifting and Moving">
                          Patient Lifting and Moving
                        </MenuItem>
                        <MenuItem value="Pediatric Safe Transport">
                          Pediatric Safe Transport
                        </MenuItem>
                        <MenuItem value="Emergency Vehicle Driver Training">
                          Emergency Vehicle Driver Training
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}

                <div className="flex flex-row-reverse">
                  <Button
                    variant="contained"
                    color="primary"
                    className="mt-4 mr-3"
                    type="submit"
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </Card>
          </Container>
        </Drawer>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[accreditationsStatus]} loading={saving} />
    </>
  )
}
